(function () {
  const ENDPOINT = "https://api.preproduct.io/api/v2/listings.json";
  const LISTING_TIMEOUT = 2500;
  const config = window.WNU_PREORDER || {};
  const cache = new Map(); // "p:<product id>" / "v:<variant id>" -> Promise<listing[]>

  class PreorderLookupError extends Error {
    constructor() {
      super("Pre-order details could not be confirmed");
      this.name = "PreorderLookupError";
    }
  }

  const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
  const isOptionalArray = (value) => value == null || Array.isArray(value);
  const isIdList = (value) =>
    Array.isArray(value) && value.every((id) => typeof id === "string" || typeof id === "number");
  // The "Estimated shipping" text PreProduct would show, or null when the data can't give one
  function shippingStatement(entry) {
    if (typeof entry.different_lead_time === "string" && entry.different_lead_time.trim() !== "") {
      return entry.different_lead_time;
    }
    if (typeof entry.lead_time === "number" && Number.isFinite(entry.lead_time) && entry.lead_time >= 0) {
      return `${entry.lead_time} days`;
    }
    return null;
  }

  const isPlanId = (value) => value == null || typeof value === "string" || typeof value === "number";
  const PLAN_FIELDS = [
    "deposit_selling_plan_id",
    "charge_later_selling_plan_id",
    "charge_upfront_selling_plan_id",
  ];

  // Only the fields used to build the line are checked, and only on the listing that applies
  function isValidListing(listing) {
    if (!isOptionalArray(listing.other_selling_plans)) return false;
    if (listing.variant_groups != null) {
      if (!isObject(listing.variant_groups)) return false;
      if (!Object.values(listing.variant_groups).every((ids) => ids == null || isIdList(ids))) return false;
    }
    if (!PLAN_FIELDS.every((field) => isPlanId(listing[field]))) return false;
    return (listing.other_selling_plans || []).every((plan) => isObject(plan) && isPlanId(plan.selling_plan_id));
  }

  // Whether a listing covers the variant: true / false, or null when its data can't tell.
  // PreProduct only launches active listings, and no specific variants means every variant.
  function appliesTo(listing, variantId) {
    if (!isObject(listing) || listing.test_status !== "active") return false;
    const variants = listing.specific_variants;
    if (variants == null) return true;
    if (!isIdList(variants)) return null;
    return !variants.length || variants.map(String).includes(variantId);
  }

  // Resolves with the listings, or rejects when PreProduct can't be reached.
  // Failures are never cached, so the next add tries again.
  function fetchListings({ productId, variantId }) {
    const key = productId ? `p:${productId}` : `v:${variantId}`;
    if (cache.has(key)) return cache.get(key);

    const params = new URLSearchParams({
      shop: config.shop || window.Shopify?.shop || "",
      any_variant: "true",
    });
    if (productId) params.set("product_ids", productId);
    else params.set("variant_ids", variantId);

    const controller = window.AbortController ? new AbortController() : null;
    let timer;
    const request = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        controller?.abort();
        reject(new PreorderLookupError());
      }, LISTING_TIMEOUT);
      fetch(`${ENDPOINT}?${params}`, controller ? { signal: controller.signal } : {})
        .then((r) => {
          if (!r.ok) throw new PreorderLookupError();
          return r.json();
        })
        .then(resolve, reject);
    })
      .then((data) => {
        const listings = Array.isArray(data) ? data : data?.preproducts;
        if (!Array.isArray(listings)) throw new PreorderLookupError();
        return listings;
      })
      .catch((error) => {
        cache.delete(key);
        throw error instanceof PreorderLookupError ? error : new PreorderLookupError();
      })
      .finally(() => clearTimeout(timer));

    cache.set(key, request);
    return request;
  }

  // The listing that covers the variant, or null. A listing that might cover it but
  // can't be read means pre-order can't be confirmed.
  function resolveListing(listings, variantId) {
    const id = String(variantId);
    let unreadable = false;
    for (const listing of listings) {
      const applies = appliesTo(listing, id);
      if (applies === null) unreadable = true;
      else if (applies) {
        if (!isValidListing(listing)) throw new PreorderLookupError();
        return listing;
      }
    }
    if (unreadable) throw new PreorderLookupError();
    return null;
  }

  // The variant group's entry in other_selling_plans, as PreProduct looks it up
  function groupPlanFor(listing, variantId) {
    const groups = listing.variant_groups || {};
    const group = Object.keys(groups).find((key) => (groups[key] || []).map(String).includes(variantId));
    if (group == null) return null;
    return (listing.other_selling_plans || []).find((plan) => plan.uid == group || plan.name == group) || null;
  }

  function groupShipping(listing, plan) {
    const has = (field) => Object.prototype.hasOwnProperty.call(plan, field);
    return {
      lead_time: has("lead_time") ? plan.lead_time : listing.lead_time,
      different_lead_time: has("different_lead_time") ? plan.different_lead_time : null,
    };
  }

  // PreProduct's default order: deposit, then charge later, then charge upfront
  const listingPlan = (listing) => PLAN_FIELDS.map((field) => listing[field]).find(Boolean) || null;

  function sellingPlanId(raw) {
    if (!raw) return null;
    return String(raw).split("SellingPlanDetail/").pop().split("SellingPlan/").pop();
  }

  // Same keys and values PreProduct writes from the PDP. As in PreProduct, the variant's
  // plan is its group plan or else the listing's plan, while "Order type" follows the listing.
  function buildLine(listing, groupPlan, shipping) {
    return {
      properties: {
        "Order type": listingPlan(listing) ? "" : "pre-order",
        "Estimated shipping": shipping,
        _preorder: "true",
      },
      selling_plan: sellingPlanId(groupPlan?.selling_plan_id || listingPlan(listing)),
    };
  }

  // The variant's pre-order line, null when it isn't on pre-order.
  // Rejects with PreorderLookupError when that can't be confirmed.
  async function lineFor(variantId, productId) {
    if (!variantId) return null;
    const listings = await fetchListings({ productId, variantId });
    let line;
    try {
      const listing = resolveListing(listings, variantId);
      if (!listing) return null;
      const groupPlan = groupPlanFor(listing, String(variantId));
      // A variant group can override the shipping fields, so check the merged result
      const shipping = shippingStatement(groupPlan ? groupShipping(listing, groupPlan) : listing);
      if (shipping === null) throw new PreorderLookupError();
      line = buildLine(listing, groupPlan, shipping);
    } catch (error) {
      cache.delete(productId ? `p:${productId}` : `v:${variantId}`);
      throw error instanceof PreorderLookupError ? error : new PreorderLookupError();
    }
    return line;
  }

  // Adds PreProduct properties to pre-order items. Pass preorder: false when the
  // product is known not to be on pre-order. Rejects with PreorderLookupError
  // rather than adding a pre-order item without its details.
  async function decorate(items, { productId, preorder } = {}) {
    if (preorder === false) return items;
    return Promise.all(
      items.map(async (item) => {
        if (item.properties?._preorder) return item;
        const line = await lineFor(item.id, productId);
        if (!line) return item;

        const decorated = {
          ...item,
          properties: { ...line.properties, ...(item.properties || {}) },
        };
        if (line.selling_plan && !decorated.selling_plan) {
          decorated.selling_plan = line.selling_plan;
        }
        return decorated;
      }),
    );
  }

  window.WNUPreorder = { lineFor, decorate, PreorderLookupError };

  // Warm the cache before PreProduct's own embed loads
  if (config.productId && config.preorder) {
    fetchListings({ productId: config.productId }).catch(() => {});
  }
})();
